import { PawnState, TILE_SIZE } from '@dreamgame/shared';
import { Graphics } from 'pixi.js';
import { getQueue, queueVersion } from '../../state/commandQueue';
import { getInterpolatedPosition, getPawn, type PawnData } from '../../state/entities';
import { getInterpolatedNpcPosition, getNpc } from '../../state/npcs';
import { selectedPawns } from '../../state/selection';
import { COLORS } from '../constants';
import { getPawnCenter } from './PawnLayer';

// ============================================================================
// PathLayer - Renders path indicators for selected pawns
// ============================================================================

let graphics: Graphics;

// Preallocated point objects
const _tempPawnCenter = { x: 0, y: 0 };
const _tempTileCenter = { x: 0, y: 0 };

export const createPathLayer = (): Graphics => {
  graphics = new Graphics();
  return graphics;
};

const getTileCenter = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): { x: number; y: number } => {
  _tempTileCenter.x = tileX * TILE_SIZE + TILE_SIZE / 2;
  _tempTileCenter.y = tileY * TILE_SIZE + TILE_SIZE / 2;
  return _tempTileCenter;
};

// Draw path line from pawn to remaining waypoints
const drawPathLine = ({
  pawnCenter,
  path,
  pathIndex,
}: {
  pawnCenter: { x: number; y: number };
  path: ReadonlyArray<{ x: number; y: number }>;
  pathIndex: number;
}): void => {
  graphics.setStrokeStyle({ width: 2, color: COLORS.PATH_LINE, alpha: 0.6 });
  graphics.moveTo(pawnCenter.x, pawnCenter.y);

  for (let i = pathIndex; i < path.length; i++) {
    const point = path[i];
    if (!point) {
      continue;
    }
    const tileCenter = getTileCenter({ tileX: point.x, tileY: point.y });
    graphics.lineTo(tileCenter.x, tileCenter.y);
  }
  graphics.stroke();
};

// Draw waypoint dots along the path
const drawWaypointDots = ({
  path,
  pathIndex,
}: {
  path: ReadonlyArray<{ x: number; y: number }>;
  pathIndex: number;
}): void => {
  for (let i = pathIndex; i < path.length - 1; i++) {
    const point = path[i];
    if (!point) {
      continue;
    }
    const tileCenter = getTileCenter({ tileX: point.x, tileY: point.y });
    graphics.setFillStyle({ color: COLORS.PATH_WAYPOINT, alpha: 0.5 });
    graphics.circle(tileCenter.x, tileCenter.y, 2);
    graphics.fill();
  }
};

// Draw destination marker
const drawDestinationMarker = ({
  targetX,
  targetY,
}: {
  targetX: number;
  targetY: number;
}): void => {
  const destX = targetX * TILE_SIZE;
  const destY = targetY * TILE_SIZE;
  const padding = 6;
  const cornerRadius = 3;

  graphics.setFillStyle({ color: COLORS.PATH_DESTINATION, alpha: 0.08 });
  graphics.roundRect(
    destX + padding,
    destY + padding,
    TILE_SIZE - padding * 2,
    TILE_SIZE - padding * 2,
    cornerRadius
  );
  graphics.fill();

  graphics.setStrokeStyle({ width: 1, color: COLORS.PATH_DESTINATION, alpha: 0.4 });
  graphics.roundRect(
    destX + padding,
    destY + padding,
    TILE_SIZE - padding * 2,
    TILE_SIZE - padding * 2,
    cornerRadius
  );
  graphics.stroke();
};

// PERFORMANCE: Preallocated point for dashed line drawing
const _dashFrom = { x: 0, y: 0 };
const _dashTo = { x: 0, y: 0 };

// Draw a dashed line between two points
const drawDashedLine = ({
  fromX,
  fromY,
  toX,
  toY,
}: {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}): void => {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) {
    return;
  }

  const dashLen = 6;
  const gapLen = 4;
  const segLen = dashLen + gapLen;
  const nx = dx / dist;
  const ny = dy / dist;

  let traveled = 0;
  while (traveled < dist) {
    const end = Math.min(traveled + dashLen, dist);
    _dashFrom.x = fromX + nx * traveled;
    _dashFrom.y = fromY + ny * traveled;
    _dashTo.x = fromX + nx * end;
    _dashTo.y = fromY + ny * end;

    graphics.moveTo(_dashFrom.x, _dashFrom.y);
    graphics.lineTo(_dashTo.x, _dashTo.y);

    traveled += segLen;
  }
};

// Draw queue marker on a target tile (same style as destination marker but blue)
const drawQueueMarker = ({ targetX, targetY }: { targetX: number; targetY: number }): void => {
  const destX = targetX * TILE_SIZE;
  const destY = targetY * TILE_SIZE;
  const padding = 6;
  const cornerRadius = 3;

  graphics.setFillStyle({ color: COLORS.QUEUE_MARKER, alpha: 0.1 });
  graphics.roundRect(
    destX + padding,
    destY + padding,
    TILE_SIZE - padding * 2,
    TILE_SIZE - padding * 2,
    cornerRadius
  );
  graphics.fill();

  graphics.setStrokeStyle({ width: 1, color: COLORS.QUEUE_MARKER, alpha: 0.5 });
  graphics.roundRect(
    destX + padding,
    destY + padding,
    TILE_SIZE - padding * 2,
    TILE_SIZE - padding * 2,
    cornerRadius
  );
  graphics.stroke();
};

// PERFORMANCE: Preallocated result for entity position resolution
const _entityPos = { x: 0, y: 0, resolved: false };

// Resolve a target entity's current interpolated world position (pixels)
// Returns preallocated object - use values immediately
const resolveEntityWorldPos = ({
  networkId,
}: {
  networkId: number;
}): { x: number; y: number; resolved: boolean } => {
  _entityPos.resolved = false;

  // Check NPCs (animals) first - most common entity target
  const npc = getNpc({ networkId });
  if (npc) {
    const pos = getInterpolatedNpcPosition({ npc });
    _entityPos.x = pos.x * TILE_SIZE + TILE_SIZE / 2;
    _entityPos.y = pos.y * TILE_SIZE + TILE_SIZE / 2;
    _entityPos.resolved = true;
    return _entityPos;
  }

  // Check pawns (enemy pawns)
  const targetPawn = getPawn({ networkId });
  if (targetPawn) {
    const pos = getInterpolatedPosition({ pawn: targetPawn });
    const center = getPawnCenter({ pos });
    _entityPos.x = center.x;
    _entityPos.y = center.y;
    _entityPos.resolved = true;
    return _entityPos;
  }

  return _entityPos;
};

// Get the world-pixel position for a queued command target, following entities if applicable
const resolveCommandTarget = ({
  cmd,
}: {
  cmd: { targetX: number; targetY: number; targetEntity: number };
}): { x: number; y: number } => {
  if (cmd.targetEntity > 0) {
    const entityPos = resolveEntityWorldPos({ networkId: cmd.targetEntity });
    if (entityPos.resolved) {
      _tempTileCenter.x = entityPos.x;
      _tempTileCenter.y = entityPos.y;
      return _tempTileCenter;
    }
  }
  return getTileCenter({ tileX: cmd.targetX, tileY: cmd.targetY });
};

// Get the tile coordinates for a queued command target, following entities if applicable
const resolveCommandTargetTile = ({
  cmd,
}: {
  cmd: { targetX: number; targetY: number; targetEntity: number };
}): { x: number; y: number } => {
  if (cmd.targetEntity > 0) {
    const npc = getNpc({ networkId: cmd.targetEntity });
    if (npc) {
      _entityPos.x = Math.floor(npc.x);
      _entityPos.y = Math.floor(npc.y);
      return _entityPos;
    }
    const targetPawn = getPawn({ networkId: cmd.targetEntity });
    if (targetPawn) {
      _entityPos.x = Math.floor(targetPawn.x);
      _entityPos.y = Math.floor(targetPawn.y);
      return _entityPos;
    }
  }
  _entityPos.x = cmd.targetX;
  _entityPos.y = cmd.targetY;
  return _entityPos;
};

// Draw extension line from path endpoint to combat target's current position
const drawCombatTargetExtension = ({ pawn }: { pawn: PawnData }): void => {
  const entityPos = resolveEntityWorldPos({ networkId: pawn.combatTarget });
  if (!entityPos.resolved) {
    return;
  }

  // Find the path endpoint (last waypoint) to extend from
  const path = pawn.path;
  let fromX: number;
  let fromY: number;
  if (path && path.length > 0) {
    const lastWp = path[path.length - 1];
    if (lastWp) {
      const tc = getTileCenter({ tileX: lastWp.x, tileY: lastWp.y });
      fromX = tc.x;
      fromY = tc.y;
    } else {
      const pos = getInterpolatedPosition({ pawn });
      const center = getPawnCenter({ pos });
      fromX = center.x;
      fromY = center.y;
    }
  } else {
    const pos = getInterpolatedPosition({ pawn });
    const center = getPawnCenter({ pos });
    fromX = center.x;
    fromY = center.y;
  }

  // Draw line from path endpoint to target entity's current position
  graphics.setStrokeStyle({ width: 2, color: COLORS.PATH_LINE, alpha: 0.6 });
  graphics.moveTo(fromX, fromY);
  graphics.lineTo(entityPos.x, entityPos.y);
  graphics.stroke();
};

// Draw queue visualization for a pawn (dashed lines + markers for queued commands)
const drawQueueVisualization = ({ pawn }: { pawn: PawnData }): void => {
  const queue = getQueue({ pawnNetworkId: pawn.networkId });
  if (queue.length === 0) {
    return;
  }

  // Determine start point for first queue line
  let prevX: number;
  let prevY: number;

  // If pawn is chasing an entity, start from that entity's current position
  if (pawn.combatTarget > 0) {
    const entityPos = resolveEntityWorldPos({ networkId: pawn.combatTarget });
    if (entityPos.resolved) {
      prevX = entityPos.x;
      prevY = entityPos.y;
    } else if (pawn.targetX !== null && pawn.targetY !== null) {
      prevX = pawn.targetX * TILE_SIZE + TILE_SIZE / 2;
      prevY = pawn.targetY * TILE_SIZE + TILE_SIZE / 2;
    } else {
      const pos = getInterpolatedPosition({ pawn });
      const center = getPawnCenter({ pos });
      prevX = center.x;
      prevY = center.y;
    }
  } else if (pawn.targetX !== null && pawn.targetY !== null) {
    prevX = pawn.targetX * TILE_SIZE + TILE_SIZE / 2;
    prevY = pawn.targetY * TILE_SIZE + TILE_SIZE / 2;
  } else {
    const pos = getInterpolatedPosition({ pawn });
    const center = getPawnCenter({ pos });
    prevX = center.x;
    prevY = center.y;
  }

  // Draw dashed lines and markers for each queued command
  graphics.setStrokeStyle({ width: 2, color: COLORS.QUEUE_LINE, alpha: 0.5 });
  for (const cmd of queue) {
    const target = resolveCommandTarget({ cmd });
    drawDashedLine({ fromX: prevX, fromY: prevY, toX: target.x, toY: target.y });
    prevX = target.x;
    prevY = target.y;
  }
  graphics.stroke();

  // Draw markers on each queue target tile (follows entity position)
  for (const cmd of queue) {
    const tile = resolveCommandTargetTile({ cmd });
    drawQueueMarker({ targetX: tile.x, targetY: tile.y });
  }
};

export const drawPaths = (): void => {
  graphics.clear();

  // Access queueVersion to create reactive dependency for queue visualization
  const _qv = queueVersion.value;
  void _qv;

  const selected = selectedPawns.value;

  for (const pawn of selected) {
    const isMoving = pawn.state === PawnState.WALKING || pawn.state === PawnState.CRAWLING;

    // Draw active path visualization
    if (isMoving && pawn.path && pawn.path.length > 0) {
      const pos = getInterpolatedPosition({ pawn });
      const pawnCenter = getPawnCenter({ pos });

      drawPathLine({ pawnCenter, path: pawn.path, pathIndex: pawn.pathIndex });
      drawWaypointDots({ path: pawn.path, pathIndex: pawn.pathIndex });

      if (pawn.combatTarget > 0 && pawn.combatState > 0) {
        // Extend path line to combat target's current position (follows moving animals)
        drawCombatTargetExtension({ pawn });
      } else if (pawn.targetX !== null && pawn.targetY !== null) {
        drawDestinationMarker({ targetX: pawn.targetX, targetY: pawn.targetY });
      }
    }

    // Draw queue visualization (regardless of current pawn state)
    drawQueueVisualization({ pawn });
  }
};
